export interface TaxesAndFeesLine {
  name: string;
  amount: number;
}

export interface TaxesAndFeesBreakdown {
  taxTotal?: number;
  /** Tax amount already included in item prices (shown with taxTotal). */
  taxIncludedTaxTotal?: number;
  taxExempt?: boolean;
  serviceCharges?: TaxesAndFeesLine[];
  /** Total service charge amount (named charges + any tender fee). */
  serviceFee?: number;
  roundUpTotal?: number;
}

export function getDisplayTaxTotal(breakdown: TaxesAndFeesBreakdown): number {
  return (breakdown.taxTotal || 0) + (breakdown.taxIncludedTaxTotal || 0);
}

/** Named fee lines plus a residual when serviceFee exceeds the named sum. */
export function getDisplayServiceFeeLines(breakdown: TaxesAndFeesBreakdown): TaxesAndFeesLine[] {
  const charges = (breakdown.serviceCharges || []).filter(charge => (charge.amount || 0) > 0);
  const namedSum = charges.reduce((sum, charge) => sum + (charge.amount || 0), 0);
  const residual = Math.round((breakdown.serviceFee || 0) - namedSum);
  if (residual > 0) {
    return [...charges, { name: 'Service Fee', amount: residual }];
  }
  return charges;
}

export function getTaxesAndFeesLabel(breakdown: TaxesAndFeesBreakdown): string {
  const hasFees = (breakdown.serviceFee || 0) > 0;
  const hasDonation = (breakdown.roundUpTotal || 0) > 0;
  if (breakdown.taxExempt) {
    if (hasFees && hasDonation) {
      return 'Fees & Donation (Tax Exempt)';
    }
    if (hasFees) {
      return 'Fees (Tax Exempt)';
    }
    if (hasDonation) {
      return 'Donation (Tax Exempt)';
    }
    return 'Tax (Exempt)';
  }
  if (hasFees && hasDonation) {
    return 'Taxes, Fees & Donation';
  }
  if (hasFees) {
    return 'Taxes & Fees';
  }
  if (hasDonation) {
    return 'Taxes & Donation';
  }
  return 'Tax';
}

export function getTaxesAndFeesTotal(breakdown: TaxesAndFeesBreakdown): number {
  return (
    getDisplayTaxTotal(breakdown) + (breakdown.serviceFee || 0) + (breakdown.roundUpTotal || 0)
  );
}

export function hasTaxesAndFeesDetails(breakdown: TaxesAndFeesBreakdown): boolean {
  return (
    getDisplayTaxTotal(breakdown) > 0 ||
    (breakdown.serviceFee || 0) > 0 ||
    (breakdown.roundUpTotal || 0) > 0 ||
    !!breakdown.serviceCharges?.length
  );
}
